import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiBell, FiLogOut, FiUser, FiMenu, FiGlobe } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { notificationsAPI, b2bAgenciesAPI, studentsAPI } from '../../services/api';
import { connectSocket, disconnectSocket } from '../../services/socket';

const Header = ({ collapsed, onMobileMenuToggle }) => {
  const { user, logout } = useAuth();
  const { activeCountry, switchCountry, canSwitch, config: crmConfig, allCountries } = useCountry();
  const countryKey = String(activeCountry || '');
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const toggleLanguage = () => i18n.changeLanguage(currentLang === 'fr' ? 'en' : 'fr');
  const navigate = useNavigate();
  const [showNotif, setShowNotif] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [notifUnreadOnly, setNotifUnreadOnly] = useState(false);
  const [notifPage, setNotifPage] = useState(1);
  const [notifHasMore, setNotifHasMore] = useState(true);
  const [notifLoading, setNotifLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Admin "global view" is OFF by default so the bell only shows the admin's
  // own notifications.  It can be toggled on for an audit-style feed.
  const [adminGlobalView, setAdminGlobalView] = useState(false);
  const notifRef = useRef(null);
  const searchRef = useRef(null);
  const notifUnreadOnlyRef = useRef(false);
  const adminGlobalRef = useRef(false);
  const currentUserIdRef = useRef(null);

  const role = String(user?.role || '').toLowerCase();
  /** Cross-user notification feed: super admin / admin only (not manager). */
  const canViewAllNotifications = role === 'super_admin' || role === 'admin';

  useEffect(() => {
    if (!canViewAllNotifications) setAdminGlobalView(false);
  }, [canViewAllNotifications]);

  useEffect(() => {
    notifUnreadOnlyRef.current = notifUnreadOnly;
  }, [notifUnreadOnly]);

  useEffect(() => {
    adminGlobalRef.current = adminGlobalView;
  }, [adminGlobalView]);

  useEffect(() => {
    currentUserIdRef.current = user?.user_id ? Number(user.user_id) : null;
  }, [user?.user_id]);

  useEffect(() => {
    setSearchQuery('');
    setSearchResults(null);
    setShowNotif(false);
    loadNotifications({ reset: true });
    const socket = connectSocket();

    const onNew = (payload) => {
      if (!payload) return;
      // Defensive per-user filter: only accept notifications addressed to the
      // currently-logged-in user, unless the admin has explicitly turned on
      // the global feed.  This prevents cross-user mailbox notifications from
      // ever appearing in another user's bell — even if a server-side emit
      // accidentally broadcasts too widely.
      const targetUid = payload.user_id != null ? Number(payload.user_id) : null;
      const myUid = currentUserIdRef.current;
      if (targetUid == null) return;
      if (!adminGlobalRef.current && myUid != null && targetUid !== myUid) {
        return;
      }
      // Respect current filter in UI.
      if (notifUnreadOnlyRef.current && payload.is_read) return;

      setNotifications((prev) => {
        const id = payload.notification_id || payload.notif_id;
        const filtered = prev.filter((n) => (n.notification_id || n.notif_id) !== id);
        return [payload, ...filtered];
      });

      // Keep unread badge in sync (fallback to recompute if server didn't send count).
      if (!payload.is_read) setUnread((u) => Number(u || 0) + 1);
    };

    const onMarkAll = () => {
      setUnread(0);
      setNotifications((prev) => {
        if (notifUnreadOnlyRef.current) return [];
        return prev.map((n) => ({ ...n, is_read: 1 }));
      });
    };

    socket.on('notification:new', onNew);
    socket.on('notification:mark_all_read', onMarkAll);

    return () => {
      socket.off('notification:new', onNew);
      socket.off('notification:mark_all_read', onMarkAll);
      disconnectSocket();
    };
  }, [countryKey, adminGlobalView]);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchResults(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async ({ reset, unreadOnlyOverride, pageOverride } = { reset: false, unreadOnlyOverride: null, pageOverride: null }) => {
    try {
      if (notifLoading) return;
      setNotifLoading(true);
      const effectiveUnreadOnly = unreadOnlyOverride === null ? notifUnreadOnly : unreadOnlyOverride;
      const nextPage = reset ? 1 : (pageOverride || (notifPage + 1));
      const params = { limit: 10, page: nextPage };
      // Default bell is strictly per-user (no cross-user leakage).
      // Admin can explicitly opt into the global audit feed via the toggle.
      if (canViewAllNotifications && adminGlobalView) params.scope = 'all';
      if (effectiveUnreadOnly) params.is_read = 0;
      const res = await notificationsAPI.getAll(params);
      const data = res.data;
      const items = data.notifications || data || [];
      setNotifications((prev) => (reset ? items : [...prev, ...items]));
      setUnread(data.unread || (Array.isArray(data.notifications) ? data.notifications.filter(n => !n.is_read).length : 0));
      setNotifPage(nextPage);
      const total = Number(data.total ?? NaN);
      if (Number.isFinite(total)) {
        setNotifHasMore(nextPage * 10 < total);
      } else {
        setNotifHasMore(items.length >= 10);
      }
    } catch (err) { /* silent */ }
    finally { setNotifLoading(false); }
  };

  const handleSearch = async (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults(null); return; }

    try {
      const [leadRes, studentRes] = await Promise.all([
        b2bAgenciesAPI.getAll({ search: q, limit: 5 }),
        studentsAPI.getAll({ search: q, limit: 5 })
      ]);
      setSearchResults({
        leads: leadRes.data.agencies || leadRes.data.leads || [],
        students: studentRes.data.students
      });
    } catch { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    try {
      // Mark-all respects the same scope as the bell:
      //   * per-user by default (does NOT touch other users' notifications)
      //   * global only when admin has explicitly enabled the global view.
      await notificationsAPI.markAllRead(canViewAllNotifications && adminGlobalView ? { scope: 'all' } : undefined);
      setNotifUnreadOnly(false);
      setNotifPage(1);
      setNotifHasMore(true);
      // Force reload with unread filter off (avoid stale state)
      loadNotifications({ reset: true, unreadOnlyOverride: false, pageOverride: 1 });
    } catch { /* silent */ }
  };

  const handleNotifClick = async (notif) => {
    try {
      await notificationsAPI.markRead(notif.notification_id || notif.notif_id);
      loadNotifications({ reset: true });
      setShowNotif(false);
      if (notif.related_type === 'lead') navigate(`/b2b-agencies/${notif.related_id}`);
      else if (notif.related_type === 'student') navigate(`/students/${notif.related_id}`);
      else if (notif.related_type === 'meeting') navigate('/meetings');
    } catch { /* silent */ }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatRole = (role) => {
    return (role || '').replace(/_/g, ' ');
  };

  const showLanguageToggle = String(activeCountry || '').toUpperCase() === 'FRANCE';

  return (
    <div className={`header ${collapsed ? 'collapsed' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        {/* Mobile hamburger */}
        <button className="mobile-menu-btn" onClick={onMobileMenuToggle}>
          <FiMenu />
        </button>

        <div className="header-search" ref={searchRef}>
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder={t('header.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearch}
          />
          {searchResults && (
            <div className="notif-dropdown" style={{ left: 0, right: 'auto', width: '100%', position: 'absolute', top: '100%' }}>
              {searchResults.leads.length > 0 && (
                <>
                  <div className="notif-dropdown-header"><h3>{t('header.leads')}</h3></div>
                  {searchResults.leads.map(l => (
                    <div key={l.lead_id} className="notif-item" onClick={() => { navigate(`/b2b-agencies/${l.lead_id}`); setSearchResults(null); setSearchQuery(''); }}>
                      <div className="notif-message"><strong>{l.ref_number}</strong> — {l.student_name}</div>
                      <div className="notif-time">{l.status} · {l.email}</div>
                    </div>
                  ))}
                </>
              )}
              {searchResults.students.length > 0 && (
                <>
                  <div className="notif-dropdown-header"><h3>{t('header.students')}</h3></div>
                  {searchResults.students.map(s => (
                    <div key={s.student_id} className="notif-item" onClick={() => { navigate(`/students/${s.student_id}`); setSearchResults(null); setSearchQuery(''); }}>
                      <div className="notif-message"><strong>{s.student_ref}</strong> — {s.first_name} {s.last_name}</div>
                      <div className="notif-time">{s.status} · {s.email}</div>
                    </div>
                  ))}
                </>
              )}
              {searchResults.leads.length === 0 && searchResults.students.length === 0 && (
                <div className="notif-item"><div className="notif-message" style={{ color: 'var(--text-muted)' }}>{t('header.noResults')}</div></div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        {showLanguageToggle && (
          <button
            type="button"
            onClick={toggleLanguage}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', fontSize: 13, fontWeight: 600,
              borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--white)', color: 'var(--text)',
              cursor: 'pointer', marginRight: 8,
            }}
            title={t('header.language')}
          >
            <FiGlobe size={14} />
            {currentLang === 'fr' ? 'FR' : 'EN'}
          </button>
        )}

        {canSwitch && (
          <div style={{ position: 'relative', marginRight: 8 }}>
            <select
              value={activeCountry}
              onChange={(e) => switchCountry(e.target.value)}
              style={{
                padding: '6px 28px 6px 10px',
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--white)',
                color: 'var(--text)',
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
              }}
              title={t('header.switchCountry')}
            >
              {allCountries.map((c) => (
                <option key={c} value={c}>{crmConfig[c]?.flag} {crmConfig[c]?.label}</option>
              ))}
            </select>
          </div>
        )}
        {!canSwitch && (
          <div style={{ marginRight: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{crmConfig[activeCountry]?.flag}</span>
            <span>{crmConfig[activeCountry]?.label}</span>
          </div>
        )}

        <div className="header-notif" ref={notifRef} onClick={() => setShowNotif(!showNotif)}>
          <FiBell size={20} />
          {unread > 0 && <span className="badge">{unread}</span>}
          {showNotif && (
            <div
              className="notif-dropdown"
              style={{ width: canViewAllNotifications ? 440 : 400 }}
            >
              <div
                className="notif-dropdown-header"
                style={{
                  padding: '10px 12px',
                  gap: 10,
                  flexWrap: 'nowrap',
                  whiteSpace: 'nowrap'
                }}
              >
                <h3 style={{ fontSize: 14, margin: 0, flexShrink: 0 }}>
                  {t('header.notifications')}
                </h3>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'nowrap',
                    marginLeft: 'auto'
                  }}
                >
                  {canViewAllNotifications && (
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontSize: 12,
                        color: 'var(--text-muted)',
                        fontWeight: 600,
                        lineHeight: 1
                      }}
                      onClick={(e) => e.stopPropagation()}
                      title={t('header.allUsersHint')}
                    >
                      <input
                        type="checkbox"
                        checked={adminGlobalView}
                        onChange={(e) => {
                          setAdminGlobalView(e.target.checked);
                          setNotifPage(1);
                          setNotifHasMore(true);
                        }}
                        style={{ width: 13, height: 13, margin: 0, cursor: 'pointer' }}
                      />
                      {t('header.allUsers')}
                    </label>
                  )}
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      fontWeight: 600,
                      lineHeight: 1
                    }}
                    onClick={(e) => e.stopPropagation()}
                    title={t('header.showUnreadOnly')}
                  >
                    <input
                      type="checkbox"
                      checked={notifUnreadOnly}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNotifUnreadOnly(checked);
                        setNotifPage(1);
                        setNotifHasMore(true);
                        loadNotifications({ reset: true, unreadOnlyOverride: checked, pageOverride: 1 });
                      }}
                      style={{ width: 13, height: 13, margin: 0, cursor: 'pointer' }}
                    />
                    {t('header.unreadOnly')}
                  </label>
                  {unread > 0 && (
                    <button
                      className="btn btn-outline"
                      onClick={(e) => { e.stopPropagation(); handleMarkAllRead(); }}
                      style={{
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {t('header.markAllRead')}
                    </button>
                  )}
                </div>
              </div>
              {notifications.length === 0 ? (
                <div className="notif-item"><div className="notif-message" style={{ color: 'var(--text-muted)' }}>{t('header.noNotifications')}</div></div>
              ) : (
                notifications.map(n => (
                  <div key={n.notification_id || n.notif_id} className={`notif-item ${!n.is_read ? 'unread' : ''}`} onClick={(e) => { e.stopPropagation(); handleNotifClick(n); }}>
                    <div className="notif-message">
                      {n.message}
                      {canViewAllNotifications && (n.target_user_name || n.created_by_name) && (
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                          {n.target_user_name ? <span>{t('header.for')} <strong>{n.target_user_name}</strong></span> : null}
                          {n.created_by_name ? <span>{n.target_user_name ? ' · ' : ''}{t('header.by')} <strong>{n.created_by_name}</strong></span> : null}
                        </div>
                      )}
                    </div>
                    <div className="notif-time">{new Date(n.created_at).toLocaleString('en-IN', { timeZone: crmConfig[activeCountry]?.defaultTimezone || 'Asia/Kolkata' })}</div>
                  </div>
                ))
              )}
              {notifHasMore && (
                <div style={{ padding: 10, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'center' }}>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={(e) => { e.stopPropagation(); loadNotifications({ reset: false }); }}
                    disabled={notifLoading}
                  >
                    {notifLoading ? t('common.loading') : t('header.loadMore')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="header-user" onClick={() => setShowUserMenu(!showUserMenu)} style={{ position: 'relative' }}>
          <div className="avatar">{getInitials(user?.first_name ? `${user.first_name} ${user.last_name}` : user?.name)}</div>
          <div className="user-info">
            <div className="user-name">{user?.first_name ? `${user.first_name} ${user.last_name}` : user?.name}</div>
            <div className="user-role">{formatRole(user?.role)}</div>
          </div>
          {showUserMenu && (
            <div className="notif-dropdown" style={{ width: 200, top: '110%' }}>
              <div className="notif-item" onClick={() => { navigate('/profile'); setShowUserMenu(false); }}>
                <div className="notif-message" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiUser /> {t('header.profile')}
                </div>
              </div>
              <div className="notif-item" onClick={() => { logout(); navigate('/login'); }}>
                <div className="notif-message" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--danger)' }}>
                  <FiLogOut /> {t('header.logout')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
